/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import {
  SelectChangeEvent,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Popover,
} from '@mui/material';
import type { ChartOptions } from 'chart.js';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { disabledModelsFor, settledIncidentConfigByIdAtom } from '../config/api';
import { ipawsColorMap } from '../Ipaws/IPAWSLayer';
import { exportChartCanvasAtom } from '../map/atoms';
import { incidentSectionStats } from './atoms';
import { postKeyToLabel } from './FeaturePopup';
import { enUS } from 'date-fns/locale';
import { incidentFiltersSelector } from './ExposureStats';
import { Download } from '@mui/icons-material';
import { sanitizeFilename } from '../map/mapExportUtils';
import { buildTimeSeriesCsvContent } from './csvUtils';

export const options: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: 8,
      right: 8,
    },
  },
  scales: {
    y: {
      title: {
        display: true,
      },
    },
    x: {
      type: 'time',
      adapters: {
        date: {
          locale: enUS,
        },
      },
      time: {
        unit: 'day',
        displayFormats: {
          hour: 'MMM d, ha',
        },
      },
    },
  },
  plugins: {
    legend: {
      labels: {
        font: {
          family:
            '"ITC Franklin Gothic", "Source Sans Pro", Open Sans, Arial, Helvetica, sans-serif',
        },
      },
      position: 'bottom' as const,
    },
    tooltip: {
      callbacks: {
        beforeTitle: context => {
          const { dataset, dataIndex } = context[0];
          // @ts-expect-error - this is a custom property
          return dataset.data?.[dataIndex]?.comments;
        },
      },
    },

    title: {
      display: true,
      position: 'bottom' as const,
      text: 'Exposure by Time',
      font: {
        family: '"ITC Franklin Gothic", "Source Sans Pro", Open Sans, Arial, Helvetica, sans-serif',
      },
    },
  },
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};
type ChartProps = {
  yAttribute: string;
  bounds?: { xmin: number; ymin: number; xmax: number; ymax: number };
  context?: string;
};

export const IncidentTimeSeriesChart = React.memo((props: ChartProps) => {
  const { yAttribute, bounds } = props;
  const { incidentId, sectionId } = useParams();

  const [chartHeight, setChartHeight] = React.useState(window.innerHeight / 4);

  const chartWrapperRef = React.useRef<HTMLDivElement>(null);
  const setExportChartCanvas = useSetAtom(exportChartCanvasAtom);

  // Create chart options based on context
  const chartOptions = React.useMemo(() => {
    const baseOptions = { ...options };
    if (props.context === 'wowcast') {
      // Update font colors for wowcast context
      baseOptions.scales = {
        ...baseOptions.scales,
        x: {
          ...baseOptions.scales?.x,
          ticks: {
            ...baseOptions.scales?.x?.ticks,
            color: 'white',
          },
          title: {
            ...baseOptions.scales?.x?.title,
            color: 'white',
          },
        },
        y: {
          ...baseOptions.scales?.y,
          ticks: {
            ...baseOptions.scales?.y?.ticks,
            color: 'white',
          },
          title: {
            ...baseOptions.scales?.y?.title,
            color: 'white',
            text: postKeyToLabel[sectionId ?? 'population'][yAttribute] ?? yAttribute ?? '',
          },
        },
      };
      baseOptions.plugins = {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins?.legend,
          labels: {
            ...baseOptions.plugins?.legend?.labels,
            color: 'white',
          },
        },
        title: {
          ...baseOptions.plugins?.title,
          color: 'white',
        },
      };
    }
    return baseOptions;
  }, [props.context, sectionId, yAttribute]);

  React.useEffect(() => {
    if (props.context === 'wowcast') {
      return;
    }
    const handleResize = () => {
      setChartHeight(window.innerHeight / 4); // Adjust this calculation as needed
    };

    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.context]);

  // Until the incident config has settled the disabled models are unknown, so
  // the query waits (a section-less query answers empty without a request)
  // rather than charting the hidden runs until the real answer replaced them.
  const incidentConfig = useAtomValue(loadable(settledIncidentConfigByIdAtom(incidentId)));
  const configPending = incidentConfig.state !== 'hasData';
  const [selectedModels, setSelectedModels] = React.useState<string[]>([]);
  // Only the selection this incident owns: the previous incident's counties
  // would aggregate this one down to rows it has none of.
  const { counties: filterByCounty, states: filterByState } = useAtomValue(
    incidentFiltersSelector(incidentId)
  );
  const disabledModels = React.useMemo(
    () =>
      sectionId && incidentConfig.state === 'hasData'
        ? disabledModelsFor(incidentConfig.data, sectionId)
        : [],
    [sectionId, incidentConfig]
  );

  const sectionStats = useAtomValue(
    loadable(
      incidentSectionStats({
        incidentId: incidentId,
        sectionId: configPending ? undefined : sectionId,
        geometry: bounds,
        counties: filterByCounty,
        states: filterByState,
        disabledModels: disabledModels,
      })
    )
  );

  const data = React.useMemo(() => {
    if (sectionStats.state !== 'hasData' || sectionStats.data === null) {
      return {
        labels: [],
        datasets: [],
      };
    }
    const sortedFeatures = [...sectionStats.data.features]
      .filter(f => {
        if (disabledModels == null || disabledModels.length === 0) {
          return true;
        }
        return disabledModels.includes(f.attributes.comments as string) === false;
      })
      .sort((a, b) => {
        return Number(a.attributes.lastupdatedon) - Number(b.attributes.lastupdatedon);
      });

    if (sortedFeatures.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const featureKeys = Object.keys(sortedFeatures[0].attributes).filter(
      f => f !== 'lastupdatedon' && f !== 'comments'
    );
    return {
      labels: sortedFeatures.map(f => String(f.attributes.lastupdatedon)),
      comments: sortedFeatures.map(f => String(f.attributes.comments ?? '')),
      datasets: featureKeys.map((k, i) => ({
        yAxisGroup: 'A',
        key: k,
        label: sectionId != null ? (postKeyToLabel?.[sectionId]?.[k] ?? k) : k,
        data: sortedFeatures.map(f => ({
          ...f.attributes,
          comments: String(f.attributes.comments ?? ''),
          y: f.attributes[k],
          // lastupdatedon is epoch-ms; the x scale is `type: 'time'`, which the
          // date-fns adapter can only parse from a number/Date, not a stringified
          // epoch (parseISO would reject it and drop every point). Keep it numeric.
          x: Number(f.attributes.lastupdatedon),
        })),

        borderColor: `rgb(${Object.values(ipawsColorMap)[i]})`,
        // borderColor: 'rgb(255, 99, 132)',
        backgroundColor: `rgba(${Object.values(ipawsColorMap)[i]}, 0.5)`,
      })),
    };
  }, [sectionStats, disabledModels, sectionId]);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const canvas = chartWrapperRef.current?.querySelector('canvas') ?? null;
      setExportChartCanvas(prev => ({ ...prev, timeSeries: canvas }));
    });
    return () => {
      cancelAnimationFrame(frame);
      setExportChartCanvas(prev => ({ ...prev, timeSeries: null }));
    };
  }, [setExportChartCanvas, data]);

  React.useEffect(() => {
    if (data.datasets.length > 0) {
      setSelectedModels(data.labels.map(d => d));
    }
  }, [data]);

  const handleFilterChange = React.useCallback(
    (e: SelectChangeEvent<string | string[]>) => {
      const value = e.target.value;

      if (value != null) {
        setSelectedModels(models => {
          if (typeof value === 'string') {
            if (value === 'all') {
              if (models.length === data.labels.length) {
                return [];
              }
              return data.labels;
            }
            if (models.includes(value)) {
              return models.filter(m => m !== e.target.value);
            }
            return [...models, value];
          }

          if (value.includes('all')) {
            if (value.length - 1 === data.labels.length) {
              return [];
            }
            return data.labels;
          }

          return value;
        });
      }
    },
    [data.labels]
  );

  const defaultLabel = `${postKeyToLabel?.[sectionId ?? 'population']?.[yAttribute]}`;

  const handleDownload = React.useCallback(() => {
    const canvas = chartWrapperRef.current?.querySelector('canvas') ?? null;
    if (!canvas) return;
    import('../map/chartExportUtils')
      .then(({ captureChartHighRes }) =>
        captureChartHighRes(canvas, 1600, 1200).then(hiRes => {
          if (!hiRes) return;
          const a = document.createElement('a');
          a.href = hiRes.toDataURL('image/png');
          a.download = sanitizeFilename(`${defaultLabel} exposed over selected model times.png`);
          a.click();
        })
      )
      .catch(console.error);
  }, [defaultLabel]);

  const handleCsvDownload = React.useCallback(() => {
    const csvContent = buildTimeSeriesCsvContent(data.datasets, defaultLabel, selectedModels);
    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = sanitizeFilename(`${defaultLabel} exposed over selected model times.csv`);
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data.datasets, defaultLabel, selectedModels]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  if ((configPending || sectionStats.state === 'loading') && sectionId) {
    return (
      <Box
        sx={{
          backgroundColor: props.context === 'wowcast' ? 'transparent' : 'white',
          borderRadius: '4px',
          p: 2,
          position: 'relative',
          minHeight: props.context === 'wowcast' ? chartHeight : 400,
          height: props.context === 'wowcast' ? chartHeight : 400,
          maxHeight: props.context === 'wowcast' ? chartHeight : 500,
          width: '100%',
        }}
      >
        <CircularProgress
          sx={{
            position: 'absolute',
            left: 'calc(50% - 20px)',
            top: 'calc(50% - 20px)',
            zIndex: 3,
          }}
          color="primary"
        />
        <Line
          options={chartOptions}
          data={{
            labels: [],
            datasets: [],
          }}
        />
      </Box>
    );
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box
      sx={{
        p: 1,
        pt: 0,
        '@media print': {
          pageBreakInside: 'avoid',
        },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          color: props.context === 'wowcast' ? 'white' : undefined,
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {defaultLabel} exposed over selected model times
        <IconButton
          data-print="hidden"
          onClick={handleClick}
          sx={{ color: props.context === 'wowcast' ? 'white' : 'inherit' }}
        >
          <Download />
        </IconButton>
      </Typography>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            handleDownload();
            handleClose();
          }}
        >
          Download as PNG
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCsvDownload();
            handleClose();
          }}
        >
          Download as CSV
        </MenuItem>
      </Popover>
      <Box
        ref={chartWrapperRef}
        sx={{
          pt: 4,
          minHeight: props.context === 'wowcast' ? chartHeight : 400,
          maxHeight: props.context === 'wowcast' ? chartHeight : 500,
          backgroundColor: props.context === 'wowcast' ? 'transparent' : 'white',
        }}
      >
        <Line
          options={chartOptions}
          data={{
            // No `labels`: the time x-axis is driven by each point's numeric
            // `x` (epoch-ms). selectedModels holds the labels as strings, so
            // compare against the stringified epoch.
            datasets: data.datasets
              .filter(d => d.key === yAttribute)
              .map(d => ({
                ...d,
                data: d.data.filter(f => {
                  return selectedModels.includes(String(f.x));
                }),
              })),
          }}
        />
      </Box>

      {props.context !== 'wowcast' && (
        <FormControl
          data-print="hidden"
          sx={{
            m: 1,
            maxWidth: 400,
            width: '100%',
            height: chartHeight,
            mt: 4,
            mb: 10,
            ml: 4,
            '@media print': { display: 'none' },
          }}
        >
          <InputLabel id="filter-models-label">Filter Models</InputLabel>
          <Select
            multiple
            input={<OutlinedInput label="Filter Models" />}
            MenuProps={MenuProps}
            value={selectedModels}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(value => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
            onChange={e => {
              handleFilterChange(e as SelectChangeEvent<string | string[]>);
            }}
          >
            <MenuItem dense key="all" value="all">
              <Checkbox checked={selectedModels.length === data.labels.length} />
              <ListItemText primary="Select All/None" />
            </MenuItem>
            {data.labels.map((d, index) => (
              <MenuItem dense key={d} value={d}>
                <Checkbox checked={selectedModels.indexOf(d) > -1} />
                <ListItemText
                  primary={`${new Date(Number(d)).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: '2-digit',
                  })} ${data?.comments?.[index] ?? ''}`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
});
